import { useQueryClient } from '@tanstack/react-query';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { selectRatePlanEntry } from '@telialt/utils/src/eshopSelectors';
import { cloneDeep } from '@telialt/utils/src/helpers';
import { useSegment } from '@telialt/utils/src/segment';

import { cancelCartQuery, getCartCache, updateCart } from '../../helpers/cartQueryUtils';
import { ESHOP_MUTATION_KEY } from '../../model';
import { apiAddRatePlan } from '../../store/api';
import { useCartQuery } from '../queries/useCartQuery';
import { resetToPreviousCartState, useMutationWithCartRefresh } from './useMutationWithCartRefresh';

export const useAddRatePlanMutation = () => {
    const segment = useSegment();
    const { t } = useTranslations();
    const queryClient = useQueryClient();
    const { cartId } = useCartQuery();
    const { mutate, isPending, error } = useMutationWithCartRefresh(
        async (ratePlanCode: string) => (await apiAddRatePlan(segment, { cartId }, ratePlanCode)).data?.cart,
        {
            mutationKey: [ESHOP_MUTATION_KEY.ADD_RATE_PLAN],
            onMutate: async (ratePlanCode: string) => {
                const cartCache = getCartCache(queryClient);
                const withNewRatePlanCode = cloneDeep(cartCache);
                const ratePlanEntry = selectRatePlanEntry(withNewRatePlanCode);
                if (ratePlanEntry) {
                    await cancelCartQuery(queryClient);
                    ratePlanEntry.product.code = ratePlanCode;
                    updateCart(queryClient, withNewRatePlanCode);
                }

                return cartCache;
            },
            onError: resetToPreviousCartState(queryClient, t['eshop.product_page.action_error']),
        },
    );

    return {
        addRatePlan: mutate,
        addRatePlanIsLoading: isPending,
        addRatePlanError: error,
    };
};
